export type DateValue = string | Temporal.PlainDate;
export type DateTimeValue = string | Temporal.PlainDateTime | Temporal.ZonedDateTime | Temporal.Instant;

const isInstantString = (value: string) => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
const isInstantValue = (value: DateTimeValue): value is string | Temporal.Instant =>
  value instanceof Temporal.Instant || typeof value === "string" && isInstantString(value);

type DatePart = "year" | "month" | "day";

const dateFormatter = (locale?: string, month: "2-digit" | "long" = "2-digit") =>
  new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month,
    day: "2-digit",
    numberingSystem: "latn",
    timeZone: "UTC",
  });

const datePartOrder = (locale?: string): DatePart[] =>
  dateFormatter(locale).formatToParts(new Date(Date.UTC(2006, 10, 22)))
    .filter((part): part is Intl.DateTimeFormatPart & { type: DatePart } =>
      part.type === "year" || part.type === "month" || part.type === "day"
    )
    .map((part) => part.type);

export const getUserToday = (): Temporal.PlainDate =>
  Temporal.Now.plainDateISO();

export const getAscaToday = (): Temporal.PlainDate =>
  Temporal.Now.plainDateISO("America/Chicago");

export const isDateBefore = (date: Temporal.PlainDate, other: Temporal.PlainDate): boolean =>
  Temporal.PlainDate.compare(date, other) < 0;

export const isDateAfter = (date: Temporal.PlainDate, other: Temporal.PlainDate): boolean =>
  Temporal.PlainDate.compare(date, other) > 0;

export const isDateOnOrBefore = (date: Temporal.PlainDate, other: Temporal.PlainDate): boolean =>
  Temporal.PlainDate.compare(date, other) <= 0;

export const isDateOnOrAfter = (date: Temporal.PlainDate, other: Temporal.PlainDate): boolean =>
  Temporal.PlainDate.compare(date, other) >= 0;

export const areDatesEqual = (
  date: Temporal.PlainDate | null | undefined,
  other: Temporal.PlainDate | null | undefined
): boolean => date == null || other == null
  ? date == null && other == null
  : Temporal.PlainDate.compare(date, other) === 0;

export const isNotFutureDate = (date: Temporal.PlainDate | null): boolean =>
  date !== null && isDateOnOrBefore(date, getUserToday());

const createPlainDate = (values: Record<DatePart, string>): Temporal.PlainDate | null => {
  const rawYear = values.year;
  if (rawYear.length !== 2 && rawYear.length !== 4) return null;

  // ASCA was founded in 1957; use a fixed 1950 cutoff for legacy two-digit years.
  const year = rawYear.length === 2
    ? `${Number(rawYear) >= 50 ? "19" : "20"}${rawYear}`
    : rawYear;

  try {
    return Temporal.PlainDate.from({
      year: Number(year),
      month: Number(values.month),
      day: Number(values.day),
    }, { overflow: "reject" });
  } catch {
    return null;
  }
};

const toPlainDate = (value: DateValue): Temporal.PlainDate => {
  if (value instanceof Temporal.PlainDate) return value;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new RangeError("Date must use YYYY-MM-DD");
  return Temporal.PlainDate.from(value);
};

const toDateTime = (value: DateTimeValue): Temporal.PlainDateTime | Temporal.ZonedDateTime => {
  if (value instanceof Temporal.Instant) {
    return value.toZonedDateTimeISO(Temporal.Now.timeZoneId());
  }
  if (value instanceof Temporal.ZonedDateTime || value instanceof Temporal.PlainDateTime) return value;
  return isInstantString(value)
    ? Temporal.Instant.from(value).toZonedDateTimeISO(Temporal.Now.timeZoneId())
    : Temporal.PlainDateTime.from(value);
};

const toPlainDateTime = (value: DateTimeValue): Temporal.PlainDateTime => {
  const dateTime = toDateTime(value);
  return dateTime instanceof Temporal.ZonedDateTime ? dateTime.toPlainDateTime() : dateTime;
};

export const getDateFormatHint = (locale?: string): string =>
  dateFormatter(locale).formatToParts(new Date(Date.UTC(2006, 10, 22)))
    .map((part) => {
      if (part.type === "year") return "YYYY";
      if (part.type === "month") return "MM";
      if (part.type === "day") return "DD";
      return part.value;
    })
    .join("");

export const parseDate = (value: string, locale?: string): Temporal.PlainDate | null => {
  const match = /^\s*(\d{1,4})([./-])(\d{1,4})\2(\d{1,4})\s*$/.exec(value);
  if (!match) return null;

  const parts = [match[1], match[3], match[4]];
  const localeOrder = datePartOrder(locale);
  const orders: DatePart[][] = [
    localeOrder,
    ["year", "month", "day"],
    ["month", "day", "year"],
    ["day", "month", "year"],
  ];
  const uniqueOrders = orders.filter((order, index) =>
    orders.findIndex((candidate) => candidate.join() === order.join()) === index
  );

  for (const order of uniqueOrders) {
    if (order[0] === "year" && parts[0].length !== 4) continue;

    const parsed = createPlainDate({
      year: parts[order.indexOf("year")],
      month: parts[order.indexOf("month")],
      day: parts[order.indexOf("day")],
    });
    if (parsed) return parsed;
  }

  return null;
};

export const elapsedYears = (
  startDate: Temporal.PlainDate,
  endDate: Temporal.PlainDate
): number => {
  const years = endDate.year - startDate.year;
  const anniversary = startDate.add({ years });

  if (years > 0 && isDateAfter(anniversary, endDate)) return years - 1;
  if (years < 0 && isDateBefore(anniversary, endDate)) return years + 1;
  return years;
};

export const formatDate = (date: DateValue, locale?: string): string => {
  try {
    return toPlainDate(date).toLocaleString(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      numberingSystem: "latn",
    });
  } catch {
    return "";
  }
};

export const formatLongDate = (date: DateValue, locale?: string): string => {
  try {
    return toPlainDate(date).toLocaleString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
      numberingSystem: "latn",
    });
  } catch {
    return "";
  }
};

export const formatMediumDate = (date: DateValue, locale?: string): string => {
  try {
    return toPlainDate(date).toLocaleString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      numberingSystem: "latn",
    });
  } catch {
    return "";
  }
};

export const formatInstant = (
  instant: Temporal.Instant,
  locale?: string,
  timeZone = Temporal.Now.timeZoneId()
): string => {
  try {
    return instant.toLocaleString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      timeZone,
      timeZoneName: "long",
    });
  } catch {
    return "";
  }
};

export const formatInstantCompact = (
  instant: Temporal.Instant,
  locale?: string,
  timeZone = Temporal.Now.timeZoneId()
): string => {
  try {
    const date = formatDate(instant.toZonedDateTimeISO(timeZone).toPlainDate(), locale);
    const time = instant.toLocaleString(locale, {
      hour: "numeric",
      minute: "2-digit",
      timeZone,
    });
    return `${date} ${time}`;
  } catch {
    return "";
  }
};

export const formatInstantISO = (instant: Temporal.Instant): string =>
  instant.round({ smallestUnit: "millisecond", roundingMode: "trunc" }).toString();

export const instantToLocalDate = (
  instant: Temporal.Instant,
  timeZone = Temporal.Now.timeZoneId()
): Temporal.PlainDate => instant.toZonedDateTimeISO(timeZone).toPlainDate();

export const startOfLocalDay = (
  date: Temporal.PlainDate,
  timeZone = Temporal.Now.timeZoneId()
): Temporal.Instant => date.toZonedDateTime(timeZone).toInstant();

export const formatDurationHumanReadable = (from: DateTimeValue, to: DateTimeValue): string => {
  const duration = isInstantValue(from) && isInstantValue(to)
    ? Temporal.Instant.from(from).until(Temporal.Instant.from(to), { largestUnit: "hour" })
    : toPlainDateTime(from).until(toPlainDateTime(to), { largestUnit: "day" });
  const seconds = Math.abs(duration.total({ unit: "second" }));
  const [value, unit] = seconds >= 86400
    ? [Math.round(seconds / 86400), "day"]
    : seconds >= 3600
      ? [Math.round(seconds / 3600), "hour"]
      : seconds >= 60
        ? [Math.round(seconds / 60), "minute"]
        : [Math.round(seconds), "second"];

  return `${value} ${unit}${value === 1 ? "" : "s"}`;
};

export default {
  elapsedYears,
  formatDate,
  formatLongDate,
  formatInstant,
  formatInstantISO,
  instantToLocalDate,
  startOfLocalDay,
  getUserToday,
  getAscaToday,
  isDateBefore,
  isDateAfter,
  isDateOnOrBefore,
  isDateOnOrAfter,
  areDatesEqual,
  isNotFutureDate,
  formatDurationHumanReadable,
  getDateFormatHint,
  parseDate,
};
